import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight } from "lucide-react";
import { resolveImg } from "@/lib/api";
import { BookButton } from "@/components/site/BookButton";
import { Reveal } from "@/components/site/Reveal";

function HeroSlideshow({ slides }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!slides || slides.length <= 1) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const t = setInterval(() => setI((v) => (v + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [slides]);
  return (
    <div className="absolute inset-0" data-testid="hero-slideshow">
      {slides.map((s, idx) => (
        <div
          key={s}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${resolveImg(s)})`,
            opacity: idx === i ? 1 : 0,
            transform: idx === i ? "scale(1.12)" : "scale(1.0)",
            transition: "opacity 1.6s ease-in-out, transform 6.5s ease-out",
          }}
        />
      ))}
    </div>
  );
}

// Hero media: the B&W clinic video plays through ~2 loops, then cross-fades into a
// slideshow of real patient photos (also triggered early if the visitor scrolls).
// Each patient photo enters in black & white and gently warms to full color.
function HeroMedia({ home }) {
  const videoRef = useRef(null);
  const loops = useRef(0);
  const images = home.hero_patient_images || [];
  const [showImages, setShowImages] = useState(false);
  const [idx, setIdx] = useState(0);
  const [warm, setWarm] = useState(false);

  const onEnded = () => {
    loops.current += 1;
    if (loops.current >= 2 && images.length) {
      setShowImages(true);
    } else {
      videoRef.current?.play().catch(() => {});
    }
  };

  const prefersReduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Robust autoplay: browsers (especially iOS Safari, and any browser in Low Power /
  // data-saver mode) block autoplay until the media is ready or the user interacts.
  // We (1) set muted via the DOM property, (2) retry on the media's own ready events,
  // and (3) fall back to the first user gesture (touch/scroll/click).
  useEffect(() => {
    if (prefersReduced) return;
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => {
      v.muted = true;
      v.defaultMuted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay();
    v.addEventListener("loadeddata", tryPlay);
    v.addEventListener("canplay", tryPlay);
    const gestureEvents = ["touchstart", "pointerdown", "click", "scroll", "keydown"];
    const onGesture = () => {
      tryPlay();
      gestureEvents.forEach((e) => window.removeEventListener(e, onGesture));
    };
    gestureEvents.forEach((e) => window.addEventListener(e, onGesture, { passive: true }));
    return () => {
      v.removeEventListener("loadeddata", tryPlay);
      v.removeEventListener("canplay", tryPlay);
      gestureEvents.forEach((e) => window.removeEventListener(e, onGesture));
    };
  }, [prefersReduced]);

  useEffect(() => {
    if (!images.length) return;
    const onScroll = () => {
      if (window.scrollY > window.innerHeight * 0.4) setShowImages(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [images.length]);

  // Advance the slideshow once patient images are showing.
  useEffect(() => {
    if (!showImages || images.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 5500);
    return () => clearInterval(t);
  }, [showImages, images.length]);

  // Warm the active photo from B&W to color on each change.
  useEffect(() => {
    if (!showImages) return;
    setWarm(false);
    const w = setTimeout(() => setWarm(true), 180);
    return () => clearTimeout(w);
  }, [idx, showImages]);

  if (!home.hero_video_url) {
    if (home.hero_slides && home.hero_slides.length > 0) return <HeroSlideshow slides={home.hero_slides} />;
    return (
      <div className="absolute inset-0 w-full h-full bg-cover bg-center kenburns" style={{ backgroundImage: `url(${resolveImg(home.hero_poster)})` }} data-testid="hero-poster" />
    );
  }

  return (
    <div className="absolute inset-0" data-testid="hero-media">
      {/* Poster is always painted first so the hero is never blank/black while video loads. */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${resolveImg(home.hero_poster)})`, filter: "grayscale(1) contrast(1.05) brightness(0.95)" }}
        data-testid="hero-poster-bg"
      />
      {!prefersReduced && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          playsInline
          preload="auto"
          poster={resolveImg(home.hero_poster)}
          onEnded={onEnded}
          data-testid="hero-video"
          style={{
            filter: "grayscale(1) contrast(1.05) brightness(0.95)",
            opacity: showImages ? 0 : 1,
            transition: "opacity 1.6s ease-in-out",
          }}
        >
          {home.hero_video_webm_url && <source src={resolveImg(home.hero_video_webm_url)} type="video/webm" />}
          <source src={resolveImg(home.hero_video_url)} type="video/mp4" />
        </video>
      )}
      {!prefersReduced && images.map((s, i) => (
        <div
          key={s}
          data-testid={`hero-patient-image-${i}`}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${resolveImg(s)})`,
            opacity: showImages && i === idx ? 1 : 0,
            filter: warm && i === idx ? "grayscale(0)" : "grayscale(1)",
            transform: showImages && i === idx ? "scale(1.08)" : "scale(1)",
            transition: "opacity 1.6s ease-in-out, filter 2.4s ease-in-out, transform 7s ease-out",
          }}
        />
      ))}
    </div>
  );
}

// Full hero section (media + headline + CTAs). Behavior and markup preserved verbatim.
export function HomeHero({ home }) {
  return (
    <section className="relative h-[100svh] w-full overflow-hidden bg-espresso" data-testid="hero-section">
      <HeroMedia home={home} />
      <div className="absolute inset-0 bg-gradient-to-b from-espresso/40 via-espresso/20 to-espresso/70" />

      <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 lg:pb-32">
        <Reveal>
          <div className="text-ivory/70 text-xs uppercase tracking-[0.35em] mb-6">{home.hero_eyebrow}</div>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="font-serif font-light text-ivory text-5xl sm:text-6xl lg:text-[6.25rem] leading-[0.98] tracking-tight max-w-4xl">
            {home.hero_headline}
          </h1>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-8 text-ivory/85 text-lg lg:text-2xl font-light max-w-xl leading-relaxed">
            {home.hero_subheading}
          </p>
        </Reveal>
        <Reveal delay={360}>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <BookButton label={home.hero_cta_primary || "Book Your Visit"} variant="dark" track_label="hero" testid="hero-book-button" />
            <Link
              to="/nervous-system"
              data-testid="hero-approach-link"
              className="inline-flex items-center gap-2 text-ivory/90 text-xs uppercase tracking-[0.2em] border-b border-ivory/40 pb-1 hover:border-ivory transition-colors"
            >
              {home.hero_cta_secondary || "Our Approach"} <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-ivory/50">
        <ArrowDown size={20} className="animate-bounce" strokeWidth={1.2} />
      </div>
    </section>
  );
}
